import {CardEnum, PlayerColor, TradeEmbargoState, TradeOfferState} from '@colonist/game-states'
import {GameSpeedSettings, TradeEmbargoControllerReader} from '@colonist/game-base-types'
import {PlayerValidator} from '../PlayerValidator'
import {TradeActionStatus, TradeOfferOpponentDisplay, TradePlayerCreatingCounterOfferStatus, TradePlayerResponseStatus} from './TradeTypes'
import {TradeUtils} from './TradeUtils'
import {ArrayUtils, RandomUtils, TimeUtils} from '@colonist/utils'

export class TradeValidator {
    readonly state: TradeOfferState
    private readonly currentTurnPlayerColor: PlayerColor
    private readonly playerStates: Map<PlayerColor, PlayerValidator>
    readonly embargoState: {[key in PlayerColor]?: TradeEmbargoState}
    private counterOffersEnabled: boolean

    constructor(state: TradeOfferState, currentTurnPlayerColor: PlayerColor, playerStates: PlayerValidator[], embargoState: {[key in PlayerColor]?: TradeEmbargoState}) {
        this.state = state
        this.currentTurnPlayerColor = currentTurnPlayerColor

        this.playerStates = new Map()
        for(const playerState of playerStates) {
            this.playerStates.set(playerState.color, playerState)
        }

        this.embargoState = embargoState
        this.counterOffersEnabled = true
    }

    getGivingResources(player: PlayerColor): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        const isMyTurn = this.currentTurnPlayerColor == player

        if(isMyTurn) return isCounterOffer ? this.state.wantedResources : this.state.offeredResources

        return isCounterOffer ? this.state.offeredResources : this.state.wantedResources
    }

    getReceivingResources(player: PlayerColor): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        const isMyTurn = this.currentTurnPlayerColor == player

        if(isMyTurn) return isCounterOffer ? this.state.offeredResources : this.state.wantedResources

        return isCounterOffer ? this.state.wantedResources : this.state.offeredResources
    }

    getRecipientColor(player: PlayerColor): PlayerColor {
        const isMyTurn = this.currentTurnPlayerColor == player
        if(isMyTurn) return PlayerColor.None
        return this.currentTurnPlayerColor
    }

    getPlayerResponseStates(): TradePlayerResponseStatus[] {
        const playerResponsesStates: TradePlayerResponseStatus[] = []
        for(const playerColor in this.state.playerResponses) {
            playerResponsesStates.push({player: Number(playerColor), responseStatus: this.state.playerResponses[playerColor]})
        }
        return playerResponsesStates
    }

    getPlayerResponseState(player: PlayerColor): TradePlayerResponseStatus | undefined {
        const playerResponse = this.state.playerResponses[player]
        if(playerResponse == undefined) return undefined
        return {player: Number(player), responseStatus: playerResponse}
    }

    getPlayersCreatingCounterOffer(): TradePlayerCreatingCounterOfferStatus[] {
        const oldPlayersCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus[] = []
        for(const playerColor in this.state.playersCreatingCounterOffer) {
            oldPlayersCreatingCounterOffer.push({player: Number(playerColor), isCreatingCounterOffer: this.state.playersCreatingCounterOffer[playerColor]})
        }
        return oldPlayersCreatingCounterOffer
    }

    getExecuteTradeActionStatus(player: PlayerColor): TradeActionStatus {
        const playerStateController = this.playerStates.get(this.currentTurnPlayerColor)
        if(playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(player != this.currentTurnPlayerColor) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.cannotExecuteOpenEndedTrade'},
            }
        }

        const requiredResources = this.getCurrentTurnPlayerOfferedResources()

        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources)
        if(!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.invalidResources'},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getAcceptTradeActionStatus(player: PlayerColor): TradeActionStatus {
        const isPlayerCurrentTurnPlayer = player == this.currentTurnPlayerColor
        if(isPlayerCurrentTurnPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.currentTurnPlayerColor, player)
        if(doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getCurrentTurnPlayerUsername()}},
            }
        }

        const requiredResources = this.getCurrentTurnPlayerWantedResources()
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources)
        if(!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.invalidResources'},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    isPlayerUnableToAcceptTrade(player: PlayerColor): boolean {
        if(player == PlayerColor.None) return false

        const isCurrentTurnPlayer = player == this.currentTurnPlayerColor
        return isCurrentTurnPlayer ? this.isCurrentTurnPlayerUnableToAcceptTrade(player) : this.isOtherPlayerUnableToAcceptTrade(player)
    }

    getTradeAutoRejectionTime(gameSpeed: GameSpeedSettings, playerColor: PlayerColor): number {
        const doIHaveNoCardsToTrade = this.doesPlayerHaveAnyCardToTrade(playerColor) == false
        if(doIHaveNoCardsToTrade) return 0

        const defaultTimeToAutoReject = TradeUtils.getDefaultTradeAutoRejectionTimeByGameSpeed(gameSpeed)

        const canPlayerAccept = this.isPlayerUnableToAcceptTrade(playerColor) == false
        if(canPlayerAccept) return defaultTimeToAutoReject

        const adjustmentFactor = 0.75
        const randomizationRange = TimeUtils.MILLISECONDS
        const timeToAutoRejectIfUnableToAccept = defaultTimeToAutoReject * adjustmentFactor
        const randomizedAutoRejectionTime = RandomUtils.randomNumberBetweenFloat(timeToAutoRejectIfUnableToAccept - randomizationRange, timeToAutoRejectIfUnableToAccept + randomizationRange)
        return randomizedAutoRejectionTime
    }

    private doesPlayerHaveAnyCardToTrade(player: PlayerColor): boolean {
        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) return true

        return playerStateController.resourceCards.getNumberOfCardsInInventory() > 0
    }

    private isCurrentTurnPlayerUnableToAcceptTrade(player: PlayerColor): boolean {
        if(this.isOpenEndedTrade()) return false
        if(this.getExecuteTradeActionStatus(player).canTakeAction) return false

        return true
    }

    private isOtherPlayerUnableToAcceptTrade(player: PlayerColor): boolean {
        if(this.isOpenEndedTrade()) return this.shouldOtherPlayerFastAutoRejectOpenEndedTrade(player)
        if(this.getAcceptTradeActionStatus(player).canTakeAction) return false

        return true
    }

    private shouldOtherPlayerFastAutoRejectOpenEndedTrade(player: PlayerColor): boolean {
        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) return false

        const requiredResources = this.getCurrentTurnPlayerWantedResources()

        if(requiredResources.length > playerStateController.resourceCards.getNumberOfCardsInInventory()) return true

        const numberOfRequiredAnyCards = ArrayUtils.countOccurenceOfValueInArray(requiredResources, CardEnum.AnyResource)
        const areAllWantedCardsWildCards = numberOfRequiredAnyCards == requiredResources.length
        if(areAllWantedCardsWildCards) return false

        const requiredResourcesWithoutAnyCard = ArrayUtils.removeAllOccurrenceFromArray(requiredResources, CardEnum.AnyResource)
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResourcesWithoutAnyCard)
        if(!doesPlayerHaveRequiredResources) return true

        return false
    }

    getProposeCounterOfferActionStatus(player: PlayerColor): TradeActionStatus {
        const doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.currentTurnPlayerColor, player)

        if(!this.counterOffersEnabled) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.state.creator == player) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getCurrentTurnPlayerUsername()}},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getRejectTradeActionStatus(player: PlayerColor): TradeActionStatus {
        const doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.currentTurnPlayerColor, player)

        if(doesCurrentTurnPlayerHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getCurrentTurnPlayerUsername()}},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getOfferComponentsToDisplay(player: PlayerColor): TradeOfferOpponentDisplay {
        if(this.isOpenEndedTrade()) return TradeOfferOpponentDisplay.AvatarContainer

        const isSpectator = player == PlayerColor.None
        if(isSpectator) return TradeOfferOpponentDisplay.AvatarContainer

        if(player == this.currentTurnPlayerColor) return TradeOfferOpponentDisplay.ResponseButtons

        return TradeOfferOpponentDisplay.AvatarContainer
    }

    isOpenEndedTrade(): boolean {
        return TradeUtils.isOpenEndedTrade(this.state.offeredResources, this.state.wantedResources)
    }

    getCurrentTurnPlayerOfferedResources(): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        return isCounterOffer ? this.state.wantedResources : this.state.offeredResources
    }

    getCurrentTurnPlayerWantedResources(): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        return isCounterOffer ? this.state.offeredResources : this.state.wantedResources
    }

    private getCurrentTurnPlayerUsername(): string {
        const currentTurnPlayerState = this.playerStates.get(this.currentTurnPlayerColor)
        return currentTurnPlayerState?.username ?? ''
    }

    disableCounterOffers() {
        this.counterOffersEnabled = false
    }
}
